import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  OptimizelyExperimentsEnum,
  Quote,
  TURNSTILE_PATHS_TO_VISIBLE,
  TurnstileFEErrorCodesEnum,
  TurnstileSiteKey,
  TurnstileSiteKeyAutomation,
  TurnstileSiteKeyBotEmulated,
  USER_HIDDEN_TURNSTILE_ERRORS,
} from "@/config/app-constant";
import { APPLICATION } from "@/constants";
import { useApplication } from "@/providers/application-context-provider";
import {
  clearTurnstileErrorCode,
  clearTurnstileToken,
  setOnLoadingForExpiredToken,
  setTurnstileErrorCode,
  setTurnstileToken,
  setUnsupportedBrowserFlag,
} from "@/store/slices/quote/quote.slice";
import handleTurnstileFeErrors from "@/utils/turnstile-error";

const useTurnstile = () => {
  const pathName = usePathname();
  const dispatch = useDispatch();
  const { turnstile } = useApplication();

  // enable Turnstile
  const normalizedPath = pathName.replace(/(^\/)|(\/$)/g, "");
  const isPathMatched = TURNSTILE_PATHS_TO_VISIBLE.includes(normalizedPath);

  const isTurnstileFeatureEnabled = turnstile?.isTurnstileEnabled ?? false;
  // const isTurnstileFeatureEnabled = true;

  const isExperimentControlGroup =
    turnstile?.isTurnstileExperimentEnabled &&
    localStorage.getItem(OptimizelyExperimentsEnum.TURNSTILE) === "control";

  const isTurnstileEnabled =
    isTurnstileFeatureEnabled &&
    isPathMatched &&
    !isExperimentControlGroup &&
    APPLICATION === Quote;

  useEffect(() => {
    if (!turnstile?.isTurnstileExperimentEnabled) {
      localStorage.removeItem(OptimizelyExperimentsEnum.TURNSTILE);
    }
  }, [turnstile?.isTurnstileExperimentEnabled]);

  const handleTurnstileToken = (token: string) => {
    dispatch(clearTurnstileErrorCode());
    dispatch(setTurnstileToken(token));
  };

  const handleError = (error: unknown) => {
    const errorCode = typeof error === "string" ? error : "unknown_error";

    dispatch(clearTurnstileToken());
    dispatch(setTurnstileErrorCode(errorCode));

    if (
      /^(300|600)\d{3}$/.test(errorCode) &&
      !USER_HIDDEN_TURNSTILE_ERRORS.includes(
        errorCode as TurnstileFEErrorCodesEnum
      )
    ) {
      handleTurnstileFeErrors(errorCode, dispatch, turnstile);
    }
  };

  const handleExpireToken = () => {
    dispatch(clearTurnstileToken());
    dispatch(setOnLoadingForExpiredToken(true));
  };

  const handleUnsupportedBrowser = () => {
    dispatch(setUnsupportedBrowserFlag(true));
  };

  const getTurnstileSiteKey = () => {
    if (!isTurnstileEnabled) return "";
    if (turnstile?.isAutomationEnabled && TurnstileSiteKeyAutomation)
      return TurnstileSiteKeyAutomation;
    if (turnstile?.isEmulateBot && TurnstileSiteKeyBotEmulated)
      return TurnstileSiteKeyBotEmulated;
    return TurnstileSiteKey ?? "";
  };

  return {
    isTurnstileEnabled,
    siteKey: getTurnstileSiteKey(),
    handleTurnstileToken,
    handleError,
    handleExpireToken,
    handleUnsupportedBrowser,
  };
};

export default useTurnstile;
